import type { EmblaCarouselType } from "embla-carousel";
import { useCallback, useEffect, useEffectEvent, useState } from "react";

export const useCarouselButtons = (
  api: EmblaCarouselType | undefined,
): {
  isDisabledPrev: boolean;
  isDisabledNext: boolean;
  handleClickPrev: () => void;
  handleClickNext: () => void;
} => {
  const [isDisabledPrev, setIsDisabledPrev] = useState(true);
  const [isDisabledNext, setIsDisabledNext] = useState(true);

  const handleClickPrev = useCallback(() => {
    if (!api) return;
    api.scrollPrev();
  }, [api]);

  const handleClickNext = useCallback(() => {
    if (!api) return;
    api.scrollNext();
  }, [api]);

  const onSelect = useEffectEvent((api: EmblaCarouselType) => {
    setIsDisabledPrev(!api.canScrollPrev());
    setIsDisabledNext(!api.canScrollNext());
  });

  useEffect(() => {
    if (api) {
      // TODO: fix set state in useEffect? setter in useEffectEvent...
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onSelect(api);
      api.on("reInit", onSelect).on("select", onSelect);
    }
  }, [api]);

  return {
    isDisabledPrev,
    isDisabledNext,
    handleClickPrev,
    handleClickNext,
  };
};

export const useCarouselIndicators = (
  api: EmblaCarouselType | undefined,
): {
  selectedIndex: number;
  indicatorValues: number[];
  handleClickIndicator: (index: number) => void;
} => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [indicatorValues, setIndicatorValues] = useState<number[]>([]);

  const handleClickIndicator = useCallback(
    (index: number) => {
      if (!api) return;
      api.scrollTo(index);
    },
    [api],
  );

  const onInit = useEffectEvent((api: EmblaCarouselType) => {
    setIndicatorValues(api.scrollSnapList());
  });

  const onSelect = useEffectEvent((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap());
  });

  useEffect(() => {
    if (api) {
      // TODO: fix set state in useEffect? setter in useEffectEvent...
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onInit(api);
      onSelect(api);
      api.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
    }
  }, [api]);

  return {
    selectedIndex,
    indicatorValues,
    handleClickIndicator,
  };
};
